import axios from 'axios';
import { getEnv } from 'utils';
import * as queryString from 'query-string';
import buildQueryString from 'utils/buildQueryString';
import mapIllustrationProps from 'utils/mapIllustrationProps';
import ApiService from 'services/ApiService';
import https from 'https';

const client = axios.create({
  baseURL: getEnv('MTG_URL_SEARCH'),
  httpsAgent: new https.Agent({
    // Only disable certificate validation in development
    rejectUnauthorized: process.env.APP_ENV !== 'development' ? true : false,
  }),
});

export default class IllustrationsService {
  static search(value, locale = 'en', query = {}) {
    if (process.env.ENABLE_NEW_SEARCH_UI === 'true') {
      query.start = query.start || 0;
      query.limit = query.limit || 10;
      return this.findAll({
        search: value.replace(/\ /g, '---'),
        page: Math.floor(query.start / query.limit) + 1,
        perPage: query.limit,
        use_new_elastic_search: true,
      });
    }
    return client.get(
      `/images/search?${buildQueryString({ q: value.toLowerCase(), lang: locale, ...query })}`,
    );
  }

  static async create(data) {
    return await ApiService.post(`illustrations/submit-new-svg`, data);
  }

  static async checkExists(data) {
    return await ApiService.post(`illustrations/check`, data);
  }

  static fetchOne(slug, locale) {
    if (process.env.ENABLE_NEW_SEARCH_UI === 'true') {
      return ApiService.get(`illustrations/match?search=${slug}&field=file_name&lang=en`);
    }
    return client.get(`/images/match?q=${slug}&field=file_name&lang=${locale}`);
  }

  static fetchRelated(id, locale) {
    if (process.env.ENABLE_NEW_SEARCH_UI === 'true') {
      return ApiService.get(
        `illustrations/mlt?image_id=${id}&limit=32&start=0&lang=en&hide_tags=true`,
      );
    }
    return client.get(`/images/mlt?image_id=${id}&limit=32&start=0&lang=${locale}&hide_tags=True`);
  }

  static async findAll(query = {}, config = {}) {
    return await ApiService.get(`illustrations?${queryString.stringify(query)}`, config);
  }

  static async getAllWithVersions(query = {}, config = {}) {
    return await ApiService.get(`illustrations/all?${queryString.stringify(query)}`, config);
  }

  static async getAllWithTags() {
    return await ApiService.get(`illustrations/all-with-tags`);
  }

  static async findVersions(id) {
    return await ApiService.get(`illustrations/${id}/version`);
  }

  static async findOneByName(name) {
    return await ApiService.get(`illustrations/${name}`);
  }

  static async findByStudyField(query = {}, locale) {
    query.use_new_elastic_search = process.env.ENABLE_NEW_SEARCH_UI === 'true';
    try {
      const response = await ApiService.get(
        `illustrations/study-field?${queryString.stringify(query)}&lang=en`,
      );

      return response.data.map((subcategory) => ({
        name: subcategory.name,
        illustrations: subcategory.illustrations.map(mapIllustrationProps),
      }));
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  static async deleteImageVersion(filename, version) {
    return await ApiService.delete(`illustrations/${filename}/version/${version}`);
  }

  static async setActiveStatus(id, active) {
    return await ApiService.post(`illustrations/${id}/set-active-status`, { active });
  }

  static async sendIllustrationToEmail(email, illustrationId) {
    return await ApiService.post(`illustrations/send-illustration-to-email`, {
      email,
      illustrationId,
    });
  }

  static async generateData(id) {
    return await ApiService.get(`illustrations/generate-data/${id}`);
  }
}
